import { useState, useEffect } from 'react'
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithPopup,
  OAuthProvider,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth'
import { orgAuth, SMARTHIRE_API, ORG_DASHBOARD, ORG_SETTINGS } from './SmartHireAuth'
import { useBookDemo } from './BookDemoContext'
import './SmartHireSignInModal.css'

export default function SmartHireOrgSignInModal({ onClose }: { onClose: () => void }) {
  const openBookDemo = useBookDemo()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [step, setStep] = useState('email') // 'email' | 'password' | 'sso' | 'loading'
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')

  // Lock body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }

  // After Firebase auth → create SmartHire session → redirect
  const createSession = async (user: any, isNewUser = false) => {
    const idToken = await user.getIdToken()
    try {
      const res = await fetch(`${SMARTHIRE_API}/session_login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ idToken, userType: 'orgUser' })
      })
      const result = await res.json()
      if (result.status === 'refresh_token') {
        // Firebase token needs refresh
        const freshToken = await user.getIdToken(true)
        const res2 = await fetch(`${SMARTHIRE_API}/session_login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ idToken: freshToken, userType: 'orgUser' })
        })
        const result2 = await res2.json()
        if (result2.status === 'failed') {
          setError('Sign-in failed. Please contact support.')
          setStep('email')
          return
        }
        window.location.href = (isNewUser || result2.status === 'force_profile') ? ORG_SETTINGS : ORG_DASHBOARD
      } else if (result.status === 'failed') {
        setError('Sign-in failed. Please contact support.')
        setStep('email')
      } else {
        window.location.href = (isNewUser || result.status === 'force_profile') ? ORG_SETTINGS : ORG_DASHBOARD
      }
    } catch {
      setError('Unable to reach SmartHire. Please try again.')
      setStep('email')
    }
  }

  // Step 1: check identity provider for the email
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setInfo('')
    setStep('loading')
    try {
      const res = await fetch(`${SMARTHIRE_API}/get_idp?u=${encodeURIComponent(email.toLowerCase())}`)
      const data = await res.json()
      if (data.status === 'failed') {
        if (data.reason === 'user_notfound') {
          setError("No account found for this email.")
        } else {
          setError('An error occurred. Please try again.')
        }
        setStep('email')
        return
      }
      const idp = data.idProvider
      await setPersistence(orgAuth, browserLocalPersistence)
      if (idp === 'EmailPassword') {
        setStep('password')
      } else {
        setStep('sso')
        // SSO via popup
        try {
          const provider = new OAuthProvider(idp)
          provider.setCustomParameters({ login_hint: email })
          const result = await signInWithPopup(orgAuth, provider)
          await createSession(result.user, (result as any).additionalUserInfo?.isNewUser)
        } catch {
          setError('SSO sign-in was cancelled or failed. Please try again.')
          setStep('email')
        }
      }
    } catch {
      setError('Could not connect to authentication service. Please try again.')
      setStep('email')
    }
  }

  // Step 2 (email/password path): sign in with password
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setInfo('')
    setStep('loading')
    try {
      const result = await signInWithEmailAndPassword(orgAuth, email.toLowerCase(), password)
      await createSession(result.user, (result as any).additionalUserInfo?.isNewUser)
    } catch (err: any) {
      const isWrongPassword = err.code === 'auth/wrong-password' ||
        err.code === 'auth/invalid-credential' ||
        err.code === 'auth/invalid-login-credentials'
      setError(isWrongPassword ? 'Incorrect password. Please try again.' : 'Sign-in failed. Please try again.')
      setStep('password')
    }
  }

  const handleForgotPassword = async () => {
    if (!email) return
    setError('')
    setInfo('')
    try {
      await sendPasswordResetEmail(orgAuth, email.toLowerCase())
      setInfo('Password reset email sent! Check your inbox.')
    } catch {
      setError('Could not send reset email. Please check the address.')
    }
  }

  const isLoading = step === 'loading' || step === 'sso'

  return (
    <div className="sh-modal-backdrop" onClick={handleBackdropClick}>
      <div className="sh-modal" role="dialog" aria-modal="true" aria-labelledby="sh-org-title">

        {/* Header */}
        <div className="sh-modal__header">
          <div className="sh-modal__title-wrap">
            <div>
              <h2 className="sh-modal__title" id="sh-org-title">Organization Sign In</h2>
              <p className="sh-modal__subtitle">Access your SmartHire account</p>
            </div>
          </div>
          <button className="sh-modal__close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M1.5 1.5l13 13M14.5 1.5l-13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="sh-modal__body">
          {info && <div className="sh-modal__info">{info}</div>}
          {error && <div className="sh-modal__error">{error}</div>}

          {/* Email step */}
          {(step === 'email' || step === 'loading') && (
            <form onSubmit={handleEmailSubmit} noValidate>
              <label className="sh-modal__label" htmlFor="org-email">Work Email</label>
              <input
                id="org-email"
                className="sh-modal__input"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                disabled={isLoading}
              />
              <button className="sh-modal__btn-primary" type="submit" disabled={isLoading}>
                {isLoading ? <span className="sh-spinner" /> : 'Continue →'}
              </button>
            </form>
          )}

          {/* Password step */}
          {step === 'password' && (
            <form onSubmit={handlePasswordSubmit} noValidate>
              <div className="sh-modal__email-chip">
                <span className="sh-modal__email-chip-icon">✉️</span>
                <span className="sh-modal__email-chip-text">{email}</span>
                <button
                  type="button"
                  className="sh-modal__inline-link"
                  onClick={() => { setStep('email'); setError(''); setInfo(''); setPassword('') }}
                >
                  Change
                </button>
              </div>
              <label className="sh-modal__label" htmlFor="org-password">Password</label>
              <input
                id="org-password"
                className="sh-modal__input"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
              />
              <div className="sh-modal__row-space">
                <button type="button" className="sh-modal__inline-link" onClick={handleForgotPassword}>
                  Forgot password?
                </button>
              </div>
              <button className="sh-modal__btn-primary" type="submit">Sign In</button>
            </form>
          )}

          {/* SSO loading */}
          {step === 'sso' && (
            <div className="sh-modal__loading-state">
              <span className="sh-spinner sh-spinner--lg" />
              <p>Opening SSO sign-in…</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sh-modal__footer">
          No account?{' '}
          <button
            className="sh-modal__footer-link"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit', textDecoration: 'underline' }}
            onClick={() => { onClose(); openBookDemo('Start Free Trial') }}
          >
            Start a free trial →
          </button>
        </div>
      </div>
    </div>
  )
}
